// src/services/LicenseService.js
// Manages module licenses per entity.
// Storage key: rms_licenses
// Schema: { [entityId]: { [moduleId]: { active: bool, expiresAt: string|null, assignedAt: string } } }

import storage from './StorageAdapter';
import auditService from './AuditService';

const STORAGE_KEY = 'rms_licenses';

// All licensable module IDs (excluding dashboard and admin which are always available)
export const LICENSABLE_MODULES = [
  'customer-risk',
  'geographical-risk',
  'bwra',
  'transaction-monitoring',
  'periodic-reporting',
];

class LicenseService {
  constructor() {
    this.listeners = [];
  }

  // --- Low-level storage ---

  _load() {
    return storage.getJSON(STORAGE_KEY, {});
  }

  async _save(data) {
    await storage.setJSON(STORAGE_KEY, data);
    this._notify();
  }

  // --- Entity license helpers ---

  /**
   * Returns license record for a single module of an entity.
   * If no record exists, returns default (unlicensed).
   */
  getModuleLicense(entityId, moduleId) {
    if (!entityId || !moduleId) return { active: false, expiresAt: null, assignedAt: null };
    const data = this._load();
    return data[entityId]?.[moduleId] ?? { active: false, expiresAt: null, assignedAt: null };
  }

  /**
   * Returns all module licenses for an entity as { moduleId: licenseRecord }.
   */
  getEntityLicenses(entityId) {
    if (!entityId) return {};
    const data = this._load();
    return data[entityId] ?? {};
  }

  /**
   * Returns true when the module is active and not expired for the entity.
   */
  isLicensed(entityId, moduleId) {
    // Dashboard and Admin are always accessible regardless of licenses
    if (!moduleId || moduleId === 'dashboard' || moduleId === 'admin') return true;
    const license = this.getModuleLicense(entityId, moduleId);
    if (!license.active) return false;
    if (license.expiresAt && new Date(license.expiresAt) < new Date()) return false;
    return true;
  }

  /**
   * Activate a module for an entity.
   * @param {string} entityId
   * @param {string} moduleId
   * @param {string|null} expiresAt  ISO date string or null for no expiry
   */
  async activateLicense(entityId, moduleId, expiresAt = null) {
    const data = this._load();
    if (!data[entityId]) data[entityId] = {};
    data[entityId][moduleId] = {
      active: true,
      expiresAt: expiresAt || null,
      assignedAt: new Date().toISOString(),
    };
    await this._save(data);
    auditService.logUpdate('Admin', 'License', entityId, { moduleId, action: 'ACTIVATE', expiresAt }, `License activated: ${moduleId}`);
    return { success: true };
  }

  /**
   * Deactivate a module for an entity.
   */
  async deactivateLicense(entityId, moduleId) {
    const data = this._load();
    if (!data[entityId]) data[entityId] = {};
    data[entityId][moduleId] = {
      active: false,
      expiresAt: null,
      assignedAt: data[entityId][moduleId]?.assignedAt ?? null,
    };
    await this._save(data);
    auditService.logUpdate('Admin', 'License', entityId, { moduleId, action: 'DEACTIVATE' }, `License deactivated: ${moduleId}`);
    return { success: true };
  }

  /**
   * Toggle a module license for an entity.
   */
  async toggleLicense(entityId, moduleId, expiresAt = null) {
    const current = this.getModuleLicense(entityId, moduleId);
    if (current.active) {
      return this.deactivateLicense(entityId, moduleId);
    } else {
      return this.activateLicense(entityId, moduleId, expiresAt);
    }
  }

  /**
   * Update only the expiry date of an existing license record.
   */
  async setExpiry(entityId, moduleId, expiresAt) {
    const data = this._load();
    if (!data[entityId]?.[moduleId]) return { success: false, error: 'No license record found' };
    data[entityId][moduleId] = { ...data[entityId][moduleId], expiresAt: expiresAt || null };
    await this._save(data);
    return { success: true };
  }

  /**
   * Returns licenses for an entity that are active and expiring within daysThreshold days.
   * @param {string} entityId
   * @param {number} daysThreshold  default 30
   * @returns {Array<{ moduleId: string, expiresAt: string, daysLeft: number }>}
   */
  getExpiringLicenses(entityId, daysThreshold = 30) {
    if (!entityId) return [];
    const data = this._load();
    const entityData = data[entityId] ?? {};
    const now = new Date();
    const result = [];
    for (const [moduleId, license] of Object.entries(entityData)) {
      if (!license.active || !license.expiresAt) continue;
      const expiry = new Date(license.expiresAt);
      if (expiry < now) continue; // already expired, not a warning
      const msLeft = expiry - now;
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
      if (daysLeft <= daysThreshold) {
        result.push({ moduleId, expiresAt: license.expiresAt, daysLeft });
      }
    }
    result.sort((a, b) => a.daysLeft - b.daysLeft);
    return result;
  }

  // --- Subscription ---

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  _notify() {
    this.listeners.forEach(l => l());
    window.dispatchEvent(new Event('rms_licenses_changed'));
  }
}

const licenseService = new LicenseService();
export default licenseService;
